import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { feesApi } from '../../services/api';
import { toast } from 'sonner';
import { IndianRupee, Save, Loader2, Trash2 } from 'lucide-react';

const num = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0);

export default function FeeStructurePage() {
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [classes, setClasses] = useState([]);
  // class_id -> { registration, tuition, transport, other_label, other_amount }
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [academicYear]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await feesApi.getStructure({ academic_year: academicYear });
      const d = res.data || {};
      setClasses(d.classes || []);
      const map = {};
      (d.items || []).forEach((it) => {
        map[it.class_id] = {
          registration: String(it.registration_fee || ''),
          tuition: String(it.tuition_fee || ''),
          transport: String(it.transport_fee || ''),
          other_label: it.other_label || '',
          other_amount: String(it.other_amount || ''),
        };
      });
      setRows(map);
    } catch (e) {
      toast.error('Failed to load fee structure');
    } finally {
      setLoading(false);
    }
  };

  const setField = (classId, key, value) => {
    setRows((prev) => ({
      ...prev,
      [classId]: { ...(prev[classId] || {}), [key]: value },
    }));
  };

  const rowTotal = (r) =>
    num(r?.registration) + num(r?.tuition) + num(r?.other_amount);

  const saveAll = async () => {
    const items = classes
      .map((c) => {
        const r = rows[c.id] || {};
        const total = rowTotal(r);
        if (total <= 0) return null;
        return {
          class_id: c.id,
          total_fee: total,
          registration_fee: num(r.registration),
          tuition_fee: num(r.tuition),
          transport_fee: 0,  // Transport is per-student now; class-level is always 0
          other_label: (r.other_label || '').trim() || null,
          other_amount: num(r.other_amount),
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      toast.error('Enter at least one fee head for a class');
      return;
    }

    setSaving(true);
    try {
      await feesApi.setStructureBulk({ academic_year: academicYear, items });
      toast.success(`Saved fee structure for ${items.length} classes`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeFor = async (classId) => {
    if (!window.confirm('Remove fee amount for this class?')) return;
    try {
      await feesApi.deleteStructureForClass(classId, academicYear);
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const grandTotal = classes.reduce((sum, c) => sum + rowTotal(rows[c.id]), 0);

  return (
    <div className="space-y-6" data-testid="fee-structure-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <IndianRupee className="h-7 w-7 text-primary" /> Fee Structure
          </h1>
          <p className="text-muted-foreground mt-1">
            Break down the annual fee per class into Registration, Tuition,
            Transport and an optional custom head. Total is auto-calculated.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Academic Year</Label>
            <Input
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              placeholder="2026-2027"
              className="w-40"
              data-testid="academic-year-input"
            />
          </div>
          <Button onClick={saveAll} disabled={saving} data-testid="save-fee-structure-button">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save All
          </Button>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Per-class fee heads — {academicYear}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            <b>Transport fees are now set per student</b> (see Students page → Edit
            → "Uses school transport"). The amount varies by route/distance, so
            it's no longer a class-level field. "Other" can be used for books,
            uniform, lab, etc.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : classes.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              No classes found. Go to Classes and create classes first.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Class</TableHead>
                    <TableHead>Registration (₹)</TableHead>
                    <TableHead>Tuition (₹)</TableHead>
                    <TableHead>Other label</TableHead>
                    <TableHead>Other amount (₹)</TableHead>
                    <TableHead>Total (₹)</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classes.map((c) => {
                    const r = rows[c.id] || {};
                    const total = rowTotal(r);
                    return (
                      <TableRow key={c.id} data-testid={`fee-row-${c.id}`}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={r.registration || ''}
                            onChange={(e) => setField(c.id, 'registration', e.target.value)}
                            placeholder="0"
                            className="w-28"
                            data-testid={`fee-reg-${c.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={r.tuition || ''}
                            onChange={(e) => setField(c.id, 'tuition', e.target.value)}
                            placeholder="0"
                            className="w-28"
                            data-testid={`fee-tuition-${c.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={r.other_label || ''}
                            onChange={(e) => setField(c.id, 'other_label', e.target.value)}
                            placeholder="e.g., Books"
                            className="w-32"
                            data-testid={`fee-other-label-${c.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={r.other_amount || ''}
                            onChange={(e) => setField(c.id, 'other_amount', e.target.value)}
                            placeholder="0"
                            className="w-28"
                            data-testid={`fee-other-amt-${c.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-semibold text-primary">
                          ₹{total.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {total > 0 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeFor(c.id)}
                              data-testid={`fee-remove-${c.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="font-semibold bg-muted/30">
                    <TableCell colSpan={5}>
                      Grand total (one student per class)
                    </TableCell>
                    <TableCell className="text-primary">
                      ₹{grandTotal.toLocaleString()}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
